<template>
  <v-dialog v-model="dialog" max-width="1000px" scrollable persistent>
    <v-card style="border-radius: 12px;">
      <v-toolbar flat color="white" class="px-6" elevation="0">
        <div class="d-flex align-center">
          <div>
            <h2 class="text-h6 font-weight-bold grey--text text--darken-4">
              Galeria de Imagens
            </h2>

            <div class="d-flex align-center">
              <span class="text-caption grey--text text--darken-1 ml-2">
                {{ productIds.length }}
                {{ productIds.length === 1 ? "produto" : "produtos" }}
                selecionado{{ productIds.length !== 1 ? "s" : "" }}
              </span>
            </div>
          </div>
        </div>

        <v-spacer />

        <v-btn
          icon
          @click="close"
          color="grey lighten-1"
          class="rounded-circle"
        >
          <v-icon color="grey darken-2" size="22">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="px-6 ">
        <v-alert type="info" dense text>
          <p class="text-caption mb-0">
            Ao fazer upload de uma nova imagem, ela receberá o nome do primeiro
            produto selecionado na tabela.
          </p>
        </v-alert>

        <v-row>
          <v-col md="7">
            <app-text-search-field
              label="Pesquisar por imagens"
              @input="handleSearchInput($event)"
              :loading="loading"
            />
          </v-col>
          <v-col md="5">
            <ProductImageUpload
              ref="imageUpload"
              :product-id="firstSelectedProductId"
              @upload-complete="loadImages()"
            />
          </v-col>
        </v-row>

        <div class="pb-4 pt-10">
          <template v-if="loading">
            <v-row>
              <v-col v-for="n in 18" :key="n" cols="12" sm="4" md="3" lg="2">
                <v-skeleton-loader
                  type="image"
                  class="gallery-image"
                  style="border-radius: 8px;"
                />
              </v-col>
            </v-row>
          </template>

          <v-row v-if="!loading && images.data.length > 0">
            <v-col
              v-for="image in images.data"
              :key="image.id"
              cols="12"
              sm="4"
              md="3"
              lg="2"
            >
              <v-card
                :class="['image-card', { selected: isImageSelected(image) }]"
                @click="selectImage(image)"
                elevation="0"
                outlined
              >
                <v-img
                  :src="getImage(image,'thumb')"
                  aspect-ratio="1"
                  contain
                  class="gallery-image"
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-2"
                      />
                    </v-row>
                  </template>
                </v-img>

                <div class="action-buttons">
                  <v-btn
                    v-if="image.has_product === 0"
                    icon
                    small
                    color="error"
                    @click.stop="deleteImage(image)"
                    class="action-btn"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </div>

                <v-chip
                  v-if="image.has_product === 0"
                  x-small
                  color="warning"
                  class="product-tag"
                  label
                >
                  Sem produto
                </v-chip>

                <div v-if="isImageSelected(image)" class="selected-overlay">
                  <v-icon color="white" size="48">mdi-check-circle</v-icon>
                </div>

                <v-card-text class="pa-2">
                  <p
                    class="caption mb-1 text-truncate font-weight-medium"
                    :title="image.name"
                  >
                    {{ image.name }}
                  </p>
                  <p
                    class="caption mb-0 grey--text text--darken-1 text-truncate"
                    :title="image.file_name"
                  >
                    {{ image.file_name }}
                  </p>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <template v-if="!loading && images.data.length === 0">
            <div class="text-center grey--text text--darken-1 pa-16">
              <v-icon size="64" color="grey lighten-1">mdi-image-off</v-icon>
              <p class="mt-4 mb-0 text-h6">Nenhuma imagem encontrada</p>
              <p class="caption mt-2 mb-0">
                Tente ajustar seus critérios de pesquisa.
              </p>
            </div>
          </template>
        </div>

        <app-pagination @click="loadImages($event)" :data="images" />
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />
        <v-btn text @click="close" color="grey darken-1">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="confirmSelection"
          :disabled="!hasSelectedImages"
          :loading="loading"
          elevation="0"
        >
          Confirmar ({{ selectedImages.length }})
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MediaMixin from "@/mixins/Media/MediaMixin";
import ProductImageUpload from "@/components/app/forms/ProductImageUpload.vue";

export default {
  components: {
    ProductImageUpload,
  },

  name: "ProductImageGalleryModal",

  mixins: [MediaMixin],

  props: {
    productIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,

      searchParams: {
        search: "",
        page: 1,
      },

      dialog: false,

      images: {
        data: [],
      },

      selectedImages: [],
      uploading: false,
    };
  },

  computed: {
    hasSelectedImages() {
      return this.selectedImages.length > 0;
    },

    firstSelectedProductId() {
      return this.productIds && this.productIds.length > 0
        ? this.productIds[0]
        : null;
    },
  },

  methods: {
    async loadImages(page = 1) {
      this.searchParams.page = page;

      this.loading = true;

      await this.$http
        .index("product/image-gallery", this.searchParams)
        .then((response) => {
          this.images = response || { data: [] };
        })
        .catch((error) => {
          this.images = { data: [] };
        });

      this.loading = false;
    },

    handleSearchInput(search) {
      this.searchParams.search = search ? search : "";

      this.loadImages(1);
    },

    selectImage(image) {
      const index = this.selectedImages.findIndex((img) => img.id === image.id);

      if (index > -1) {
        this.selectedImages.splice(index, 1);
      } else {
        this.selectedImages.push(image);
      }
    },

    isImageSelected(image) {
      return this.selectedImages.some((img) => img.id === image.id);
    },

    async confirmSelection() {
      if (this.selectedImages.length === 0) return;

      this.loading = true;

      try {
        const imageIds = this.selectedImages.map((img) => img.id);

        await this.$http.$post("product/mass-associate-images", {
          product_ids: this.productIds,
          image_ids: imageIds,
        });

        this.$emit("updated");

        this.close();
      } catch (error) {
        console.error("Erro ao associar imagens:", error);
      } finally {
        this.loading = false;
      }
    },

    async deleteImage(image) {
      this.loading = true;

      try {
        await this.$http.destroy(`product/image-gallery`, image.id);

        await this.loadImages(this.searchParams.page);
      } catch (error) {
        console.error("Erro ao deletar imagem:", error);
      } finally {
        this.loading = false;
      }
    },

    open() {
      this.dialog = true;
      this.loadImages(1);
    },

    close() {
      this.dialog = false;
      this.searchParams.search = "";
      this.selectedImages = [];
    },
  },
};
</script>

<style scoped>
.image-card {
  cursor: pointer;
  outline: solid 1px transparent;
  position: relative;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #00000014;
}

.image-card:hover {
  background-color: #fafafa;
  outline: solid 1px #999;
}

.image-card:hover .action-buttons {
  opacity: 1;
}

.gallery-image {
  background-color: #fafafa;
}

.action-buttons {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.action-btn {
  background-color: white !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.product-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.selected-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(25, 118, 210, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  outline: solid 1px #1976d2;
}
</style>
